import { ApiError, del, get, patch, post } from '@/lib/api';
import {
  bool,
  dayToIso,
  num,
  numOrNull,
  rec,
  str,
  strOrNull,
  toDay,
  unwrapList
} from './normalize';
import type {
  EntryFilters,
  EntrySort,
  Paginated,
  PersonalHoursRow,
  PersonalHoursPeriod,
  TimeEntry,
  TimeEntryInput,
  TimerSession
} from '@/types';

export function toTimeEntry(v: unknown): TimeEntry {
  const e = rec(v);
  return {
    id: str(e.id),
    date: toDay(e.date),
    clientId: str(e.clientId),
    activityId: str(e.activityId),
    userId: str(e.userId),
    // pg numeric arrives as a string
    time: num(e.time ?? e.hours),
    misc: strOrNull(e.misc),
    hourlyRate: numOrNull(e.hourlyRate),
    invoiced: bool(e.invoiced),
    invoiceId: strOrNull(e.invoiceId),
    userName: strOrNull(e.userName),
    userEmail: strOrNull(e.userEmail)
  };
}

export interface EntriesQuery {
  page: number;
  filters: EntryFilters;
  sort: EntrySort;
}

function entriesQueryParams({ page, filters, sort }: EntriesQuery) {
  const scope = filters.scope ?? 'me';
  return {
    page,
    showAll: scope === 'all' ? 'true' : undefined,
    userId: scope !== 'all' && scope !== 'me' ? scope : undefined,
    hideInvoiced: filters.hideInvoiced ? 'true' : undefined,
    filterClientId: filters.clientId && filters.clientId !== 'all' ? filters.clientId : undefined,
    filterActivityId:
      filters.activityId && filters.activityId !== 'all' ? filters.activityId : undefined,
    dateFrom: filters.dateFrom ? dayToIso(filters.dateFrom) : undefined,
    dateTo: filters.dateTo ? dayToIso(filters.dateTo) : undefined,
    searchText: filters.search || undefined,
    sortField: sort.field ?? undefined,
    sortOrder: sort.field ? sort.order : undefined
  };
}

export async function fetchEntries(
  query: EntriesQuery,
  signal?: AbortSignal
): Promise<Paginated<TimeEntry>> {
  const raw = rec(await get<unknown>('/api/daycards', entriesQueryParams(query), signal));
  const pagination = rec(raw.pagination);
  return {
    items: unwrapList(raw, 'daycards', 'entries').map(toTimeEntry),
    page: num(pagination.page, query.page),
    pageSize: num(pagination.pageSize, 50),
    totalCount: num(pagination.totalCount),
    totalPages: num(pagination.totalPages, 1)
  };
}

/** The read-only "what the employee originally typed" table. */
export async function fetchOriginalEntries(
  query: EntriesQuery,
  signal?: AbortSignal
): Promise<Paginated<TimeEntry>> {
  const raw = rec(await get<unknown>('/api/original-daycards', entriesQueryParams(query), signal));
  const pagination = rec(raw.pagination);
  return {
    items: unwrapList(raw, 'daycards', 'entries').map(toTimeEntry),
    page: num(pagination.page, query.page),
    pageSize: num(pagination.pageSize, 50),
    totalCount: num(pagination.totalCount),
    totalPages: num(pagination.totalPages, 1)
  };
}

export const createEntry = (input: TimeEntryInput) =>
  post<unknown>('/api/daycards', {
    clientId: input.clientId,
    activityId: input.activityId,
    time: input.time,
    misc: input.misc ?? undefined,
    date: dayToIso(input.date),
    invoiced: input.invoiced ?? false,
    userId: input.userId
  }).then(toTimeEntry);

export type EntryPatch = Partial<{
  clientId: string;
  activityId: string;
  time: number;
  misc: string | null;
  date: string;
  invoiced: boolean;
  invoiceId: string | null;
  hourlyRate: number | null;
}>;

export const updateEntry = (id: string, input: EntryPatch) =>
  patch<unknown>(`/api/daycards/${id}`, {
    ...input,
    date: input.date ? dayToIso(input.date) : undefined
  }).then(toTimeEntry);

export const deleteEntry = (id: string) => del(`/api/daycards/${id}`);

export const bulkDeleteEntries = (daycardIds: string[]) =>
  post('/api/daycards/bulk-delete', { daycardIds });

export const markEntriesInvoiced = (entryIds: string[], invoiceId: string) =>
  post('/api/daycard-entries/mark-invoiced', { entryIds, invoiceId });

/** Uninvoiced entries for a client — the pool the invoice editor draws from. */
export async function fetchClientUninvoicedEntries(clientId: string, signal?: AbortSignal) {
  const raw = await get<unknown>(
    `/api/clients/${clientId}/entries`,
    { invoiced: false },
    signal
  );
  return unwrapList(raw, 'entries').map(toTimeEntry);
}

/**
 * POST /api/invoices refuses, with a 400 and this code, to build an invoice
 * from an entry that is already on another one. Returns the offending ids, or
 * null when the error is anything else. The list the user picked from was
 * stale; the caller's job is to refetch it and say so.
 */
export function entriesAlreadyInvoiced(err: unknown): string[] | null {
  if (!(err instanceof ApiError) || err.status !== 400) return null;
  const body = err.body;
  if (!body || typeof body !== 'object') return null;
  const b = body as { error?: unknown; entryIds?: unknown };
  if (b.error !== 'entries_already_invoiced') return null;
  return Array.isArray(b.entryIds) ? b.entryIds.filter((id) => typeof id === 'string') : [];
}

// -------------------------------------------------------------------- timer

function toTimerSession(v: unknown): TimerSession {
  const s = rec(v);
  return {
    id: str(s.id),
    clientId: str(s.clientId),
    clientName: strOrNull(s.clientName),
    activityId: str(s.activityId),
    activityName: strOrNull(s.activityName),
    note: strOrNull(s.note),
    startedAt: str(s.startedAt),
    stoppedAt: strOrNull(s.stoppedAt),
    elapsedSeconds: num(s.elapsedSeconds)
  };
}

export async function fetchTimer(signal?: AbortSignal): Promise<TimerSession | null> {
  const raw = rec(await get<unknown>('/api/timer', undefined, signal));
  if (!raw.running || !raw.session) return null;
  return toTimerSession(raw.session);
}

export const startTimer = (input: { clientId: string; activityId: string; note?: string }) =>
  post<unknown>('/api/timer/start', input).then((r) => toTimerSession(rec(r).session ?? r));

export async function stopTimer() {
  const raw = rec(await post<unknown>('/api/timer/stop'));
  return {
    session: toTimerSession(raw.session),
    entry: raw.entry ? toTimeEntry(raw.entry) : null
  };
}

// ----------------------------------------------------------- personal hours

export async function fetchPersonalHours(
  params: { userId: string; period: PersonalHoursPeriod },
  signal?: AbortSignal
): Promise<PersonalHoursRow[]> {
  const raw = await get<unknown>('/api/personal-hours', params, signal);
  return unwrapList(raw, 'entries').map((v) => {
    const r = rec(v);
    return {
      id: str(r.id),
      date: toDay(r.date),
      activityName: strOrNull(r.activityName),
      clientName: strOrNull(r.clientName),
      time: num(r.time),
      hourlyRate: num(r.hourlyRate),
      amount: num(r.amount)
    };
  });
}
